import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Error as ErrorIcon,
  SecurityOutlined as SecurityIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import penaltyService from '../services/penaltyService';
import PenaltyExecutionSummary from '../components/disputes/PenaltyExecutionSummary';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  pending:            'warning',
  draft:              'default',
  scheduled:          'info',
  applied:            'error',
  failed:             'error',
  disputed:           'warning',
  waived:             'success',
  collected:          'error',
  cancelled:          'default',
  refunded:           'success',
  pending_collection: 'warning',
};

const PenaltyDetailPage = () => {
  const { penaltyId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['penalties', 'common']);
  const { user } = useAuth();

  const [penalty, setPenalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPenalty = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await penaltyService.get(penaltyId);
        if (response?.data) {
          setPenalty(response.data);
        } else if (response) {
          setPenalty(response);
        } else {
          setError(t('penalties:detail.notFound', 'Pénalité introuvable'));
        }
      } catch (err) {
        console.error('Failed to load penalty:', err);
        setError(err?.response?.data?.message || t('penalties:detail.error', 'Erreur lors du chargement de la pénalité'));
      } finally {
        setLoading(false);
      }
    };

    if (penaltyId) {
      fetchPenalty();
    }
  }, [penaltyId, t]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {t('common:loading', 'Chargement...')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !penalty) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/penalties')}
            sx={{ mb: 2 }}
          >
            {t('common:back', 'Retour')}
          </Button>
        </Box>
        <Alert severity="error">
          <AlertTitle>{t('common:error', 'Erreur')}</AlertTitle>
          {error || t('penalties:detail.notFound', 'Pénalité introuvable')}
        </Alert>
      </Container>
    );
  }

  const isSanctioned = penalty.user_id === user?.id || penalty.user_id === user?.userId;
  const roleLabel = isSanctioned
    ? t('penalties:roles.sanctioned', 'Sanctionné')
    : t('penalties:roles.beneficiary', 'Bénéficiaire');
  const roleColor = isSanctioned ? 'error' : 'success';

  const statusKey = penalty.status || 'pending';
  const statusLabel = t(`penalties:status.${statusKey}`, statusKey);
  const statusColor = STATUS_COLORS[statusKey] || 'default';

  const penaltyTypeKey = penalty.penalty_type || '';
  const typeLabel = t(`penalties:types.${penaltyTypeKey}`, penaltyTypeKey.replace(/_/g, ' '));

  const amount = penalty.amount != null
    ? `${Number(penalty.amount).toFixed(2)} ${penalty.currency || 'EUR'}`
    : '—';

  const listingTitle = penalty?.reservation?.listing?.title
    ?? penalty?.reservation?.Listing?.title
    ?? null;

  const createdAt = penalty?.created_at
    ? format(new Date(penalty.created_at), i18n.language === 'fr' ? 'dd/MM/yyyy HH:mm' : 'MM/dd/yyyy HH:mm', {
        locale: i18n.language === 'fr' ? fr : enUS,
      })
    : '—';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back button */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/penalties')}
        >
          {t('common:back', 'Retour aux pénalités')}
        </Button>
      </Box>

      {/* Header card */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(45deg, #1976d2 30%, #0d47a1 90%)', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              {typeLabel}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              ID: {penalty.id}
            </Typography>
            {listingTitle && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('penalties:listing', 'Annonce')} : {listingTitle}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {amount}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip label={statusLabel} color={statusColor} />
              <Chip label={roleLabel} color={roleColor} variant="outlined" />
            </Stack>
          </Box>
        </Box>
      </Paper>

      {/* Main details card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {t('penalties:detail.information', 'Informations générales')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Left column */}
            <Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('penalties:detail.type', 'Type')}
                  </Typography>
                  <Typography variant="body1">{typeLabel}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('penalties:detail.status', 'Statut')}
                  </Typography>
                  <Chip label={statusLabel} color={statusColor} />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('penalties:detail.role', 'Votre rôle')}
                  </Typography>
                  <Chip label={roleLabel} color={roleColor} variant="outlined" />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('penalties:detail.createdAt', 'Date de création')}
                  </Typography>
                  <Typography variant="body2">{createdAt}</Typography>
                </Box>
              </Stack>
            </Box>

            {/* Right column */}
            <Box>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('penalties:detail.amount', 'Montant')}
                  </Typography>
                  <Typography variant="h6" color="error.main" fontWeight={700}>
                    {amount}
                  </Typography>
                </Box>

                {penalty?.reservation?.listing && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('penalties:listing', 'Annonce')}
                    </Typography>
                    <Typography variant="body2">
                      {listingTitle}
                    </Typography>
                  </Box>
                )}

                {penalty?.reason && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('penalties:detail.reason', 'Raison')}
                    </Typography>
                    <Typography variant="body2">{penalty.reason}</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Penalty execution summary */}
      {penalty && (
        <Box sx={{ mb: 3 }}>
          <PenaltyExecutionSummary penalty={penalty} dispute={penalty.dispute} />
        </Box>
      )}
    </Container>
  );
};

export default PenaltyDetailPage;
